import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { AlertTriangle, Plus, FileX, Camera, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import OccurrenceEvidence from "@/components/OccurrenceEvidence";
import OccurrenceTreatment from "@/components/OccurrenceTreatment";

const STATUS_FLOW = [
  { value: "open", label: "Aberto", tone: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "in_progress", label: "Em andamento", tone: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "resolved", label: "Resolvido", tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { value: "validated", label: "Validado", tone: "bg-stone-200 text-stone-800" },
];

const PRIORITY = [
  { value: "low", label: "Baixa", tone: "bg-stone-100 text-stone-700" },
  { value: "medium", label: "Média", tone: "bg-amber-100 text-amber-800" },
  { value: "high", label: "Alta", tone: "bg-orange-100 text-orange-800" },
  { value: "critical", label: "Crítica", tone: "bg-rose-100 text-rose-800" },
];

export default function Occurrences() {
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "medium",
    unit_id: "",
    assignee_id: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/occurrences");
      setItems(data);
    } catch (err) {
      console.error("Failed to load occurrences", err);
    }
  };

  useEffect(() => {
    load();
    api.get("/units").then(({ data }) => setUnits(data)).catch(() => {});
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return items;
    return items.filter((o) => o.status === statusFilter);
  }, [items, statusFilter]);

  const counts = useMemo(
    () =>
      items.reduce(
        (acc, o) => ({ ...acc, [o.status]: (acc[o.status] || 0) + 1, all: acc.all + 1 }),
        { all: 0 }
      ),
    [items]
  );

  const create = async () => {
    if (!form.title.trim()) {
      toast.error("Título obrigatório");
      return;
    }
    try {
      const payload = { ...form };
      if (!payload.unit_id) delete payload.unit_id;
      if (!payload.assignee_id) delete payload.assignee_id;
      await api.post("/occurrences", payload);
      setOpenNew(false);
      setForm({ title: "", description: "", priority: "medium", unit_id: "", assignee_id: "" });
      toast.success("Ocorrência criada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar");
    }
  };

  const advanceStatus = async (occ, newStatus, extra = {}) => {
    try {
      await api.patch(`/occurrences/${occ.id}`, { status: newStatus, ...extra });
      toast.success("Status atualizado");
      load();
      setSelected(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao atualizar");
    }
  };

  const statusBadge = (status) => {
    const s = STATUS_FLOW.find((x) => x.value === status);
    return (
      <Badge variant="outline" className={s?.tone || ""}>{s?.label || status}</Badge>
    );
  };

  const priorityBadge = (p) => {
    const pr = PRIORITY.find((x) => x.value === p);
    return <Badge className={`${pr?.tone || ""} border-none`}>{pr?.label || p}</Badge>;
  };

  return (
    <Layout
      title="Não Conformidades"
      subtitle="Ocorrências operacionais e seu fluxo de resolução"
      actions={
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="occurrence-new">
              <Plus className="h-4 w-4 mr-2" /> Nova ocorrência
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">Nova ocorrência</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Título *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="mt-1 bg-white"
                  data-testid="occurrence-title"
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1 bg-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prioridade</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="mt-1 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITY.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unidade</Label>
                  <Select value={form.unit_id || "none"} onValueChange={(v) => setForm({ ...form, unit_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="mt-1 bg-white">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {units.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Responsável</Label>
                <Select value={form.assignee_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1 bg-white">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="occurrence-submit">
                Criar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      <div data-testid="occurrences-page" className="space-y-4">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="bg-stone-100 border border-stone-200">
            <TabsTrigger value="all">Todas ({counts.all || 0})</TabsTrigger>
            {STATUS_FLOW.map((s) => (
              <TabsTrigger key={s.value} value={s.value} data-testid={`tab-${s.value}`}>
                {s.label} ({counts[s.value] || 0})
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={statusFilter} className="mt-4">
            {filtered.length === 0 ? (
              <Card className="p-10 text-center text-stone-500 font-body rounded-xl border-stone-200 bg-white">
                <FileX className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                Nenhuma ocorrência neste filtro
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((o) => (
                  <Card
                    key={o.id}
                    className="p-5 rounded-xl border-stone-200 bg-white cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all"
                    onClick={() => setSelected(o)}
                    data-testid={`occurrence-card-${o.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {priorityBadge(o.priority)}
                          {statusBadge(o.status)}
                          {o.source === "checklist" && (
                            <Badge variant="outline" className="border-emerald-200 text-emerald-800 text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" /> Auto
                            </Badge>
                          )}
                        </div>
                        <h4 className="font-display font-bold tracking-tight line-clamp-2">{o.title}</h4>
                        <p className="text-xs text-stone-500 font-body mt-1 line-clamp-2">{o.description}</p>
                        {o.ai_summary && (
                          <div
                            className="mt-2 flex items-start gap-1.5 rounded-md bg-rose-50 border border-rose-200 px-2 py-1.5 text-[11px] text-rose-800 font-body"
                            data-testid={`occurrence-ai-summary-${o.id}`}
                          >
                            <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <span className="line-clamp-3"><b>Análise IA:</b> {o.ai_summary}</span>
                          </div>
                        )}
                        <div className="mt-3 flex items-center gap-3 text-xs text-stone-400 font-body">
                          <span>{formatDateTime(o.created_at)} · {o.created_by_name || "—"}</span>
                          {o.photos?.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-emerald-800" data-testid={`occurrence-photo-count-${o.id}`}>
                              <Camera className="h-3.5 w-3.5" /> {o.photos.length} foto{o.photos.length > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                {selected && priorityBadge(selected.priority)}
                {selected?.title}
              </DialogTitle>
            </DialogHeader>
            {selected && (
              <div className="space-y-3 text-sm font-body">
                <div className="text-xs text-stone-500">
                  Criado em {formatDateTime(selected.created_at)} por {selected.created_by_name || "—"}
                </div>
                <div className="p-3 rounded-md bg-stone-50 border border-stone-200 whitespace-pre-line">
                  {selected.description || "—"}
                </div>
                {selected.ai_summary && (
                  <div className="flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800" data-testid="occurrence-detail-ai-summary">
                    <Sparkles className="h-4 w-4 shrink-0 mt-0.5" />
                    <span><b>Análise IA:</b> {selected.ai_summary}</span>
                  </div>
                )}
                <OccurrenceEvidence occurrence={selected} />
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-stone-500">Status</div>
                    <div className="font-semibold">{statusBadge(selected.status)}</div>
                  </div>
                  <div>
                    <div className="text-stone-500">Responsável</div>
                    <div className="font-semibold">{selected.assignee_name || "—"}</div>
                  </div>
                </div>

                <OccurrenceTreatment
                  occurrence={selected}
                  onAdvance={(status, extra) => advanceStatus(selected, status, extra)}
                  onSaved={(updated) => {
                    setSelected(updated);
                    setItems((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
                  }}
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
